import type { PublicPreviewDto } from './publicDto';

// Ad Preview Mockups Stage 1 §13.12. Plain-text dump of all Meta +
// Google copy authored on the row, presented as readable lists for
// quick scan + easy copy. No mockup chrome, no ad-type tags — just
// the text.
//
// Hidden when both Meta and Google blocks are empty (the parent
// PreviewPage handles that gate via shouldShow).

type Props = { dto: PublicPreviewDto };

// Exported for the parent so it can gate tab visibility without
// duplicating the empty-check logic.
export function textVariationsHasContent(dto: PublicPreviewDto): boolean {
  const metaHas =
    !!dto.meta &&
    (dto.meta.headlines.length > 0 ||
      dto.meta.bodyCopy.length > 0 ||
      dto.meta.ctaButton !== null);
  const googleHas =
    !!dto.google &&
    (dto.google.headlines.length > 0 ||
      dto.google.descriptions.length > 0 ||
      dto.google.path1.length > 0);
  return metaHas || googleHas;
}

export function TextVariationsTab({ dto }: Props) {
  const showMeta =
    !!dto.meta &&
    (dto.meta.headlines.length > 0 ||
      dto.meta.bodyCopy.length > 0 ||
      dto.meta.ctaButton !== null);
  const showGoogle =
    !!dto.google &&
    (dto.google.headlines.length > 0 ||
      dto.google.descriptions.length > 0 ||
      dto.google.path1.length > 0);

  return (
    <div className="space-y-8">
      {showMeta && <MetaBlock dto={dto} />}
      {showGoogle && <GoogleBlock dto={dto} />}
    </div>
  );
}

function MetaBlock({ dto: fullDto }: { dto: PublicPreviewDto }) {
  // Only rendered when the server included Meta (showMeta guards the call).
  const dto = { ...fullDto, meta: fullDto.meta! };
  return (
    <section className="rounded-md border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-4 py-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
          Meta Ad Copy
        </h2>
      </header>
      <div className="p-4 space-y-4 text-sm">
        {dto.meta.headlines.length > 0 && (
          <BulletList title="Headlines" items={dto.meta.headlines} />
        )}
        {dto.meta.bodyCopy.length > 0 && (
          <BulletList title="Body Copy" items={dto.meta.bodyCopy} />
        )}
        {dto.meta.ctaButton && (
          <p>
            <span className="font-semibold text-slate-700">CTA:</span>{' '}
            <span className="text-slate-900">{dto.meta.ctaButton}</span>
          </p>
        )}
      </div>
    </section>
  );
}

function GoogleBlock({ dto }: { dto: PublicPreviewDto }) {
  if (!dto.google) return null;
  const { google } = dto;
  // Display URL composed lazily here so the Text Variations tab
  // mirrors what GoogleSearchMockup shows. Not extracted into a
  // shared helper because the two callers compose slightly
  // differently (mockup wants host only; here we want the full
  // resolved finalUrl for copy/paste).
  const displayUrl = google.finalUrl ?? null;
  return (
    <section className="rounded-md border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-4 py-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
          Google Ad Copy
        </h2>
      </header>
      <div className="p-4 space-y-4 text-sm">
        {google.headlines.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-700 mb-1">
              Headlines (with pin positions)
            </h3>
            <ol className="list-decimal list-inside text-slate-900 space-y-0.5">
              {google.headlines.map((h, i) => (
                <li key={i} className="leading-snug">
                  {h.text}
                  {h.pinPosition !== null && (
                    <span className="ml-2 text-xs text-slate-500">
                      (Pinned to slot {h.pinPosition})
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
        {google.descriptions.length > 0 && (
          <BulletList title="Descriptions" items={google.descriptions} />
        )}
        {displayUrl && (
          <p className="break-all">
            <span className="font-semibold text-slate-700">Display URL:</span>{' '}
            <span className="text-slate-900 font-mono text-xs">
              {displayUrl}
            </span>
          </p>
        )}
        {(google.path1 || google.path2) && (
          <p>
            <span className="font-semibold text-slate-700">Paths:</span>{' '}
            <span className="text-slate-900 font-mono text-xs">
              /{google.path1}
              {google.path2 ? `/${google.path2}` : ''}
            </span>
          </p>
        )}
      </div>
    </section>
  );
}

function BulletList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h3 className="font-semibold text-slate-700 mb-1">{title}</h3>
      <ul className="list-disc list-inside text-slate-900 space-y-0.5">
        {items.map((text, i) => (
          <li key={i} className="leading-snug whitespace-pre-wrap">
            {text}
          </li>
        ))}
      </ul>
    </div>
  );
}
